import { useState } from 'react';
import type { FloorPlanConfig } from './FloorPlanGenerator';

interface Props {
  onComplete: (config: Partial<FloorPlanConfig>) => void;
  onClose: () => void;
}

type FamilySize = 'single' | 'couple' | 'small-family' | 'large-family' | 'multi-gen';
type Lifestyle = 'work-home' | 'entertaining' | 'outdoor' | 'minimalist' | 'pet-owner';
type Budget = 'starter' | 'mid-range' | 'premium' | 'luxury';

const FAMILY_OPTIONS = [
  { id: 'single' as FamilySize, label: 'Single', icon: 'ri-user-line', bedrooms: 1, bathrooms: 1 },
  { id: 'couple' as FamilySize, label: 'Couple', icon: 'ri-user-heart-line', bedrooms: 1, bathrooms: 1 },
  { id: 'small-family' as FamilySize, label: 'Small Family', subtitle: '1-2 kids', icon: 'ri-parent-line', bedrooms: 3, bathrooms: 2 },
  { id: 'large-family' as FamilySize, label: 'Large Family', subtitle: '3+ kids', icon: 'ri-team-line', bedrooms: 4, bathrooms: 3 },
  { id: 'multi-gen' as FamilySize, label: 'Multi-generational', icon: 'ri-group-line', bedrooms: 5, bathrooms: 3 },
];

const LIFESTYLE_OPTIONS = [
  { id: 'work-home' as Lifestyle, label: 'Work from Home', icon: 'ri-computer-line', extras: ['home-office', 'walk-in-closet'] },
  { id: 'entertaining' as Lifestyle, label: 'Frequent Entertaining', icon: 'ri-restaurant-line', extras: ['living-dining', 'pantry'], kitchen: 'open' as const },
  { id: 'outdoor' as Lifestyle, label: 'Outdoor Living', icon: 'ri-plant-line', extras: ['balcony', 'pool'] },
  { id: 'minimalist' as Lifestyle, label: 'Minimalist', icon: 'ri-layout-line', extras: [] },
  { id: 'pet-owner' as Lifestyle, label: 'Pet Owner', icon: 'ri-bear-smile-line', extras: ['laundry', 'garage'] },
];

const BUDGET_OPTIONS = [
  { id: 'starter' as Budget, label: 'Starter', range: '< $200k', icon: 'ri-home-smile-line', area: '50–80m²', styles: ['Ranch', 'Bungalow', 'Tiny Home'] },
  { id: 'mid-range' as Budget, label: 'Mid-Range', range: '$200k - $500k', icon: 'ri-home-4-line', area: '80–100m²', styles: ['Colonial', 'Cape Cod', 'Farmhouse'] },
  { id: 'premium' as Budget, label: 'Premium', range: '$500k - $1M', icon: 'ri-home-5-line', area: '100–120m²', styles: ['Contemporary', 'Mediterranean', 'Mid-Century Modern'] },
  { id: 'luxury' as Budget, label: 'Luxury', range: '$1M+', icon: 'ri-building-2-line', area: '> 150m²', styles: ['Victorian', 'Georgian', 'Minimalist'] },
];

export default function SmartWizard({ onComplete, onClose }: Props) {
  const [step, setStep] = useState(1);
  const [familySize, setFamilySize] = useState<FamilySize | null>(null);
  const [lifestyle, setLifestyle] = useState<Lifestyle[]>([]);
  const [budget, setBudget] = useState<Budget | null>(null);

  const selectedFamily = FAMILY_OPTIONS.find(f => f.id === familySize);
  const selectedLifestyles = LIFESTYLE_OPTIONS.filter(l => lifestyle.includes(l.id));
  const selectedBudget = BUDGET_OPTIONS.find(b => b.id === budget);

  const canProceed = () => {
    if (step === 1) return familySize !== null;
    if (step === 2) return lifestyle.length > 0;
    if (step === 3) return budget !== null;
    return true;
  };

  const toggleLifestyle = (id: Lifestyle) => {
    setLifestyle(prev => prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]);
  };

  const generateConfig = (): Partial<FloorPlanConfig> => {
    const allExtras = new Set<string>();
    selectedLifestyles.forEach(l => l.extras.forEach(e => allExtras.add(e)));

    // Add budget-based extras
    if (budget === 'premium' || budget === 'luxury') {
      allExtras.add('walk-in-closet');
      allExtras.add('pantry');
    }
    if (budget === 'luxury') {
      allExtras.add('gym');
      allExtras.add('basement');
    }

    const kitchenType = selectedLifestyles.find(l => l.kitchen)?.kitchen || 'open';

    return {
      rooms: [
        { type: 'Bedroom', count: selectedFamily?.bedrooms || 3, icon: 'ri-hotel-bed-line' },
        { type: 'Bathroom', count: selectedFamily?.bathrooms || 2, icon: 'ri-drop-line' },
        { type: 'Living Room', count: 1, icon: 'ri-sofa-line' },
        { type: 'Kitchen', count: 1, icon: 'ri-knife-line' },
      ],
      area: selectedBudget?.area || '100–120m²',
      extras: Array.from(allExtras),
      kitchenType,
      prompt: `Design a ${selectedBudget?.label.toLowerCase() || 'modern'} home for a ${selectedFamily?.label.toLowerCase() || 'family'} with ${lifestyle.map(l => LIFESTYLE_OPTIONS.find(opt => opt.id === l)?.label.toLowerCase()).join(', ')} lifestyle`,
    };
  };

  const handleApply = () => {
    const config = generateConfig();
    onComplete(config);
  };

  const progress = (step / 4) * 100;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-teal-200 px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <i className="ri-magic-line text-xl text-teal-500"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Smart Wizard</h3>
                <p className="text-xs text-gray-400">Step {step} of 4</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          {/* Progress Bar */}
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-400 to-cyan-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] bg-gray-50">
          {/* Step 1: Family Size */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-2">What&apos;s your family size?</h4>
                <p className="text-sm text-gray-500">This helps us determine the number of bedrooms and bathrooms</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {FAMILY_OPTIONS.map(option => (
                  <button
                    key={option.id}
                    onClick={() => setFamilySize(option.id)}
                    className={`p-5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                      familySize === option.id
                        ? 'border-teal-400 bg-teal-50'
                        : 'border-gray-200 bg-white hover:border-teal-200'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        familySize === option.id ? 'bg-teal-100' : 'bg-gray-100'
                      }`}>
                        <i className={`${option.icon} text-2xl ${familySize === option.id ? 'text-teal-500' : 'text-gray-400'}`}></i>
                      </div>
                      <div className="flex-1">
                        <h5 className="text-gray-900 font-semibold mb-1">{option.label}</h5>
                        {option.subtitle && <p className="text-xs text-gray-400 mb-2">{option.subtitle}</p>}
                        <p className="text-xs text-gray-400">
                          {option.bedrooms} bedroom{option.bedrooms > 1 ? 's' : ''} · {option.bathrooms} bathroom{option.bathrooms > 1 ? 's' : ''}
                        </p>
                      </div>
                      {familySize === option.id && (
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-checkbox-circle-fill text-xl text-teal-500"></i>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Lifestyle */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-2">What&apos;s your lifestyle?</h4>
                <p className="text-sm text-gray-500">Select all that apply — we&apos;ll add the right features</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {LIFESTYLE_OPTIONS.map(option => (
                  <button
                    key={option.id}
                    onClick={() => toggleLifestyle(option.id)}
                    className={`p-5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                      lifestyle.includes(option.id)
                        ? 'border-teal-400 bg-teal-50'
                        : 'border-gray-200 bg-white hover:border-teal-200'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        lifestyle.includes(option.id) ? 'bg-teal-100' : 'bg-gray-100'
                      }`}>
                        <i className={`${option.icon} text-2xl ${lifestyle.includes(option.id) ? 'text-teal-500' : 'text-gray-400'}`}></i>
                      </div>
                      <div className="flex-1">
                        <h5 className="text-gray-900 font-semibold mb-2">{option.label}</h5>
                        {option.extras.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {option.extras.map(extra => (
                              <span key={extra} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full whitespace-nowrap">
                                {extra.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {lifestyle.includes(option.id) && (
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-checkbox-circle-fill text-xl text-teal-500"></i>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Budget */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-2">What&apos;s your budget tier?</h4>
                <p className="text-sm text-gray-500">This determines the size and recommended styles</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {BUDGET_OPTIONS.map(option => (
                  <button
                    key={option.id}
                    onClick={() => setBudget(option.id)}
                    className={`p-5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                      budget === option.id
                        ? 'border-teal-400 bg-teal-50'
                        : 'border-gray-200 bg-white hover:border-teal-200'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        budget === option.id ? 'bg-teal-100' : 'bg-gray-100'
                      }`}>
                        <i className={`${option.icon} text-2xl ${budget === option.id ? 'text-teal-500' : 'text-gray-400'}`}></i>
                      </div>
                      <div className="flex-1">
                        <h5 className="text-gray-900 font-semibold mb-1">{option.label}</h5>
                        <p className="text-xs text-teal-600 mb-2 font-medium">{option.range}</p>
                        <p className="text-xs text-gray-400 mb-2">Area: {option.area}</p>
                        <div className="flex flex-wrap gap-1">
                          {option.styles.slice(0, 2).map(style => (
                            <span key={style} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full whitespace-nowrap">
                              {style}
                            </span>
                          ))}
                        </div>
                      </div>
                      {budget === option.id && (
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-checkbox-circle-fill text-xl text-teal-500"></i>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Summary */}
          {step === 4 && (
            <div className="space-y-5">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Your Configuration Summary</h4>
                <p className="text-sm text-gray-500">Review your auto-filled floor plan settings</p>
              </div>

              <div className="space-y-3">
                {/* Family Size */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                      <i className={`${selectedFamily?.icon} text-lg text-teal-500`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Family Size</p>
                      <p className="text-gray-900 font-semibold">{selectedFamily?.label}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <i className="ri-hotel-bed-line text-gray-400"></i>
                      <span className="text-gray-700">{selectedFamily?.bedrooms} Bedrooms</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-drop-line text-gray-400"></i>
                      <span className="text-gray-700">{selectedFamily?.bathrooms} Bathrooms</span>
                    </div>
                  </div>
                </div>

                {/* Lifestyle */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                      <i className="ri-heart-pulse-line text-lg text-cyan-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Lifestyle</p>
                      <p className="text-gray-900 font-semibold">{selectedLifestyles.map(l => l.label).join(', ')}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(new Set(selectedLifestyles.flatMap(l => l.extras))).map(extra => (
                      <span key={extra} className="px-2.5 py-1 bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs rounded-full whitespace-nowrap">
                        {extra.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Budget */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                      <i className={`${selectedBudget?.icon} text-lg text-violet-500`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Budget Tier</p>
                      <p className="text-gray-900 font-semibold">{selectedBudget?.label} <span className="text-violet-500 text-sm">({selectedBudget?.range})</span></p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm mb-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-ruler-line text-gray-400"></i>
                      <span className="text-gray-700">Area: {selectedBudget?.area}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedBudget?.styles.map(style => (
                      <span key={style} className="px-2.5 py-1 bg-violet-50 border border-violet-200 text-violet-700 text-xs rounded-full whitespace-nowrap">
                        {style}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Kitchen Type */}
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <i className="ri-knife-line text-xl text-teal-500"></i>
                    <div>
                      <p className="text-xs text-gray-400">Kitchen Layout</p>
                      <p className="text-gray-900 font-semibold capitalize">{selectedLifestyles.find(l => l.kitchen)?.kitchen || 'Open'} Kitchen</p>
                    </div>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-500"></i>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-1"></i> {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                canProceed()
                  ? 'bg-gradient-to-r from-teal-400 to-cyan-500 text-white hover:shadow-lg hover:shadow-teal-400/30'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next <i className="ri-arrow-right-line ml-1"></i>
            </button>
          ) : (
            <button
              onClick={handleApply}
              className="px-6 py-2.5 bg-gradient-to-r from-teal-400 to-cyan-500 text-white rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-teal-400/30 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line mr-1"></i> Apply &amp; Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}